import React, { useEffect, useState } from 'react';
import { getSetting, setSetting, listTreks, deleteTrek, saveTrek } from '../lib/db';
import { buildDemoTrek } from '../lib/simulator';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import OfflineBadge from '../components/OfflineBadge';
import { toast } from 'sonner';
import { Info, TestTube, Trash, DownloadSimple } from '@phosphor-icons/react';

export default function Settings() {
  const [simulator, setSim] = useState(false);
  const [intervalMs, setIntervalMs] = useState(3000);
  const [deviation, setDeviation] = useState(25);
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    (async () => {
      setSim(!!(await getSetting('simulator', false)));
      setIntervalMs((await getSetting('intervalMs', 3000)) || 3000);
      setDeviation((await getSetting('deviationThreshold', 25)) || 25);
    })();
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const toggleSim = async (v) => {
    setSim(v);
    await setSetting('simulator', v);
    toast(v ? 'Simulator ON' : 'Simulator OFF');
  };

  const changeInterval = async (v) => {
    const n = v[0];
    setIntervalMs(n);
    await setSetting('intervalMs', n);
  };

  const changeDeviation = async (v) => {
    const n = v[0];
    setDeviation(n);
    await setSetting('deviationThreshold', n);
  };

  const loadDemo = async () => {
    await saveTrek(buildDemoTrek());
    toast.success('Demo trek added to My Treks');
  };

  const clearAll = async () => {
    if (!window.confirm('Delete ALL saved treks? This cannot be undone.')) return;
    const list = await listTreks();
    for (const t of list) await deleteTrek(t.id);
    toast('All treks deleted');
  };

  const doInstall = async () => {
    if (!installEvt) return toast('Use your browser menu to Add to Home Screen.');
    installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    if (outcome === 'accepted') toast.success('TrailBack installed');
    setInstallEvt(null);
  };

  return (
    <div className="min-h-[100dvh] pb-28">
      <header className="safe-top px-5 pt-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Preferences</div>
          <h1 className="font-display font-black text-3xl" data-testid="settings-title">Settings</h1>
        </div>
        <OfflineBadge />
      </header>

      <div className="px-5 mt-6 space-y-4">
        <Row testId="row-install">
          <div>
            <div className="font-display font-bold">Install app</div>
            <div className="text-xs text-zinc-400 mt-0.5">Launch full-screen like a native app.</div>
          </div>
          <button onClick={doInstall} data-testid="install-button" className="btn-tap px-4 h-10 rounded-lg bg-[#FF9F1C] text-black font-semibold text-sm inline-flex items-center gap-1.5">
            <DownloadSimple size={16} weight="bold" /> Install
          </button>
        </Row>

        <Row testId="row-simulator">
          <div>
            <div className="font-display font-bold flex items-center gap-1.5"><TestTube size={16} weight="fill" className="text-emerald-400" /> GPS Simulator</div>
            <div className="text-xs text-zinc-400 mt-0.5">Test route recording without walking outside.</div>
          </div>
          <Switch checked={simulator} onCheckedChange={toggleSim} data-testid="simulator-toggle" />
        </Row>

        <Row testId="row-interval" col>
          <div className="flex items-center justify-between">
            <div className="font-display font-bold">Tracking interval</div>
            <div className="font-mono-stat text-sm">{(intervalMs / 1000).toFixed(1)}s</div>
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 mb-3">Higher = better battery, less detail.</div>
          <Slider value={[intervalMs]} min={1000} max={15000} step={500} onValueChange={changeInterval} data-testid="interval-slider" />
        </Row>

        <Row testId="row-deviation" col>
          <div className="flex items-center justify-between">
            <div className="font-display font-bold">Deviation threshold</div>
            <div className="font-mono-stat text-sm">{deviation} m</div>
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 mb-3">Warn when you drift this far from your recorded trail.</div>
          <Slider value={[deviation]} min={10} max={100} step={5} onValueChange={changeDeviation} data-testid="deviation-slider" />
        </Row>

        <Row testId="row-demo">
          <div>
            <div className="font-display font-bold">Load demo trek</div>
            <div className="text-xs text-zinc-400 mt-0.5">Adds a sample trek for Return-to-Start testing.</div>
          </div>
          <button onClick={loadDemo} data-testid="load-demo-settings" className="btn-tap px-4 h-10 rounded-lg bg-white/10 font-semibold text-sm">Load</button>
        </Row>

        <Row testId="row-clear">
          <div>
            <div className="font-display font-bold text-rose-400">Clear all treks</div>
            <div className="text-xs text-zinc-400 mt-0.5">Wipes local storage. Cannot be undone.</div>
          </div>
          <button onClick={clearAll} data-testid="clear-all-treks" className="btn-tap px-3 h-10 rounded-lg bg-rose-500/15 border border-rose-500/40 text-rose-300 font-semibold text-sm inline-flex items-center gap-1.5">
            <Trash size={16} weight="bold" /> Clear
          </button>
        </Row>

        <div className="glass-panel rounded-2xl p-4 mt-4">
          <div className="flex items-start gap-2">
            <Info size={16} className="text-[#FFD166] mt-0.5" />
            <div className="text-xs text-zinc-300 leading-relaxed">
              <div className="font-display font-bold text-white mb-1">How TrailBack works</div>
              • <span className="text-white">GPS tracking</span> — browser Geolocation API samples your position at the interval above.<br/>
              • <span className="text-white">Offline storage</span> — routes are saved in IndexedDB on this device. No server upload.<br/>
              • <span className="text-white">Return route</span> — the recorded polyline is reversed; guidance points to the nearest point on that line.<br/>
              • <span className="text-white">Compass</span> — device orientation is used when supported; otherwise GPS movement heading.<br/>
              • <span className="text-white">Testing</span> — enable the Simulator to walk a virtual route.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ children, col = false, testId }) {
  return (
    <div data-testid={testId} className={`glass-panel rounded-2xl px-4 py-3 ${col ? '' : 'flex items-center justify-between gap-4'}`}>
      {children}
    </div>
  );
}
